import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Header } from "@/components/Header";
import { AudioPlayer } from "@/components/AudioPlayer";
import { ShareModal } from "@/components/ShareModal";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Wifi, CircleDot, Flag, Share2, ArrowRight, MessageSquare } from "lucide-react";

export default function ShowcaseProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/public/showcase/${userId}`)
      .then((r) => setP(r.data))
      .catch(() => setP(null))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="min-h-screen bg-ink flex items-center justify-center"><span className="font-mono text-xs uppercase tracking-[0.3em] text-ash animate-pulse">Loading…</span></div>;
  if (!p) return <div className="min-h-screen bg-ink flex items-center justify-center text-ash">Profile not found.</div>;

  const avatar = mediaUrl(p.avatar);

  // Signed-in members go to the real, gated profile with messaging; visitors go sign up.
  const connect = () => navigate(user ? `/profile/${userId}` : "/auth");
  const connectLabel = user ? "Open the full HausCard" : "Join WriterHaus to connect";

  return (
    <div className="min-h-screen bg-ink">
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} url={`${process.env.REACT_APP_BACKEND_URL}/api/share/${userId}`} storyUrl={`${process.env.REACT_APP_BACKEND_URL}/api/og/story/${userId}.png`} title={`${p.name} · WriterHaus`} text={`Check out ${p.name} on WriterHaus`} />
      <Header />
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-24 pb-20">
        <div className="mb-6 flex items-center justify-between gap-4">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Shared HausCard</span>
          <button onClick={() => setShareOpen(true)} data-testid="share-card-btn" className="inline-flex items-center gap-2 border border-line text-cream font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:border-gold hover:text-gold transition-colors">
            <Share2 size={14} /> Share
          </button>
        </div>

        <div className="grid lg:grid-cols-[360px_1fr] gap-px bg-line border border-line">
          <div className="bg-surface p-7 flex flex-col gap-6">
            <div className="aspect-square bg-ink border border-line overflow-hidden">
              {avatar ? <img src={avatar} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
            </div>
            <h1 className="font-heading text-4xl font-bold tracking-tighter uppercase text-cream leading-none">{p.name}</h1>
            <div className="flex flex-col gap-2 font-mono text-xs uppercase tracking-wide text-ash">
              {p.location && <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-gold" /> {p.location}</span>}
              {p.remote_friendly && <span className="inline-flex items-center gap-2"><Wifi size={14} className="text-gold" /> Remote friendly</span>}
              <span className="inline-flex items-center gap-2"><CircleDot size={14} className="text-gold" /> {p.open_to_collaborate ? "Open to collaborate" : "Not currently open"}</span>
            </div>
            <button onClick={connect} data-testid="showcase-unlock-btn" className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-gold-hi transition-colors">
              {connectLabel} <ArrowRight size={15} />
            </button>
          </div>

          <div className="bg-surface p-7 md:p-9 flex flex-col gap-8">
            {p.intro_video && (
              <div>
                <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-4">Intro</h2>
                <video src={mediaUrl(p.intro_video)} controls playsInline data-testid="showcase-intro-video" className="w-full aspect-video bg-ink border border-line" />
              </div>
            )}
            {p.bio && <p className="text-lg text-cream leading-relaxed font-light">{p.bio}</p>}
            <div className="flex flex-wrap gap-2">
              {(p.roles || []).map((r) => <span key={r} className="border border-line px-3 py-1 font-mono text-[11px] uppercase text-ash">{r}</span>)}
            </div>
            <div className="flex flex-wrap gap-2">
              {(p.genres || []).map((g) => <span key={g} className="font-mono text-[11px] uppercase tracking-wider text-gold/80">{g}</span>)}
            </div>

            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-4">Songs</h2>
              {p.songs?.length ? (
                <div className="flex flex-col gap-3" data-testid="showcase-songs">
                  {p.songs.map((s, i) => <AudioPlayer key={s.id} song={s} index={i} />)}
                </div>
              ) : <p className="text-ash text-sm">No songs uploaded yet.</p>}
            </div>

            {p.gallery?.length > 0 && (
              <div>
                <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-4">Gallery</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-line border border-line" data-testid="showcase-gallery">
                  {p.gallery.map((g) => <div key={g.id} className="aspect-square bg-ink overflow-hidden"><img src={mediaUrl(g.media)} alt="" className="w-full h-full object-cover media-grayscale" /></div>)}
                </div>
              </div>
            )}

            {/* Collaborate CTA — connecting stays inside the Haus */}
            <div data-testid="showcase-collab-cta" className="border border-dashed border-line bg-ink p-7 text-center">
              <MessageSquare size={22} className="mx-auto mb-3 text-gold" />
              <p className="font-heading text-lg font-bold uppercase tracking-tight text-cream mb-2">Want to write with {p.name?.split(" ")[0] || "them"}?</p>
              <p className="text-ash text-sm mb-5">Members can request an intro referencing the exact song they loved, and search the whole Haus for new collaboration opportunities.</p>
              <button onClick={connect} data-testid="showcase-locked-cta" className="inline-flex items-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-gold-hi transition-colors">
                {connectLabel} <ArrowRight size={14} />
              </button>
            </div>

            <button onClick={() => toast("Reported. Our moderation queue will review this profile.")} className="self-start inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-ash hover:text-destructive transition-colors"><Flag size={13} /> Report this profile</button>
          </div>
        </div>
      </div>
    </div>
  );
}
